import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

function labelFor(row) {
  if (!row) return '';
  const code = row.code || '';
  const description = row.description || row.name || '';
  if (code && description && description !== code) return `${code} — ${description}`;
  return code || description || '';
}

export default function CostCodeTypeahead({
  value,
  onChange,
  projectVuid,
  selectedLabel,
  placeholder = 'Search cost codes...',
  className = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800',
  disabled = false,
  required = false,
  name = 'cost_code_vuid',
  projectCodesOnly = false,
  choices = null,
  emptyMessage = 'No cost codes match',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState(selectedLabel || '');
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (selectedLabel) setLabel(selectedLabel);
  }, [selectedLabel, value]);

  useEffect(() => {
    if (!value) {
      setLabel('');
      return;
    }
    if (selectedLabel) return;
    if (Array.isArray(choices)) {
      const row = choices.find((item) => item.vuid === value);
      if (row) setLabel(labelFor(row));
      return;
    }
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/cost-codes`, {
      params: {
        vuid: value,
        project_vuid: projectVuid || undefined,
        scope: projectCodesOnly ? 'project' : undefined,
        page: 1,
        per_page: 1,
      },
    }).then((response) => {
      if (cancelled) return;
      const row = (response.data && response.data.items && response.data.items[0]) || null;
      if (row) setLabel(labelFor(row));
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [value, projectVuid, selectedLabel, projectCodesOnly, choices]);

  useEffect(() => {
    const onDoc = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const search = (term) => {
    if (Array.isArray(choices)) {
      const q = (term || '').trim().toLowerCase();
      setRows(choices.filter((row) => !q || labelFor(row).toLowerCase().includes(q)));
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/cost-codes`, {
      params: {
        q: term || undefined,
        page: 1,
        per_page: 25,
        status: 'active',
        project_vuid: projectVuid || undefined,
        scope: projectCodesOnly ? 'project' : undefined,
      },
    }).then((response) => {
      setRows((response.data && response.data.items) || []);
    }).catch(() => {
      setRows([]);
    }).finally(() => setLoading(false));
  };

  const handleFocus = () => {
    setOpen(true);
    search(query);
  };

  const handleQuery = (term) => {
    setQuery(term);
    setOpen(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(term), 200);
  };

  const pick = (row) => {
    setLabel(labelFor(row));
    setQuery('');
    setOpen(false);
    if (onChange) onChange(row.vuid, row);
  };

  const clear = () => {
    setLabel('');
    setQuery('');
    if (onChange) onChange('', null);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        name={name}
        value={open ? query : (label || '')}
        disabled={disabled}
        required={required && !value}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        onFocus={handleFocus}
        onChange={(event) => handleQuery(event.target.value)}
      />
      {value && !disabled ? (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-sm"
          onClick={clear}
          aria-label="Clear cost code"
        >
          ×
        </button>
      ) : null}
      {open && (
        <div className="absolute z-[9999] mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</div>
          ) : (
            rows.map((row) => (
              <button
                type="button"
                key={row.vuid}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(row)}
              >
                {labelFor(row)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
